import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Icon from "../AppIcon";
import Button from "./Button";
import AnimatedLogo from "../AnimatedLogo";
import LanguageSwitcher from "../LanguageSwitcher";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureFlags();

  const navigationItems = [
    { name: t("navigation.home"), path: "/homepage", icon: "Home" },
    {
      name: t("navigation.serviceEcosystem"),
      path: "/service-ecosystem",
      icon: "Layers",
    },
    {
      name: t("navigation.discoveryPortal"),
      path: "/discovery-portal",
      icon: "Search",
    },
    ...(isFeatureEnabled("productsNavigation")
      ? [
          {
            name: t("navigation.productsPortfolio"),
            path: "/products-portfolio",
            icon: "Package",
          },
        ]
      : []),
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border elevation-subtle"
          : "bg-background"
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-6 lg:px-8">
          {/* Logo */}
          <Link
            to="/homepage"
            className="transition-smooth hover:opacity-80"
            onClick={closeMobileMenu}
          >
            <AnimatedLogo />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                  isActivePath(item.path)
                    ? "text-accent bg-accent/10"
                    : "text-text-primary hover:text-accent hover:bg-muted"
                }`}
              >
                <Icon name={item.icon} size={16} />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Desktop CTA & Language Switcher */}
          <div className="hidden lg:flex items-center space-x-4">
            <LanguageSwitcher />
            <Button
              variant="outline"
              size="sm"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => window.open("/discovery-portal", "_self")}
            >
              {t("navigation.scheduleCall")}
            </Button>
            <Button
              variant="default"
              size="sm"
              className="bg-cta hover:bg-cta/90 text-cta-foreground"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => window.open("/discovery-portal", "_self")}
            >
              {t("navigation.startDiscovery")}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-md text-text-primary hover:text-accent hover:bg-muted transition-smooth"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-background border-t border-border">
            <div className="px-6 py-4 space-y-3">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-smooth ${
                    isActivePath(item.path)
                      ? "text-accent bg-accent/10"
                      : "text-text-primary hover:text-accent hover:bg-muted"
                  }`}
                >
                  <Icon name={item.icon} size={20} />
                  <span>{item.name}</span>
                </Link>
              ))}

              <div className="pt-4 space-y-3 border-t border-border">
                <div className="flex justify-center mb-4">
                  <LanguageSwitcher />
                </div>
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={() => {
                    closeMobileMenu();
                    window.open("/discovery-portal", "_self");
                  }}
                >
                  {t("navigation.scheduleCall")}
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  className="bg-cta hover:bg-cta/90 text-cta-foreground"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => {
                    closeMobileMenu();
                    window.open("/discovery-portal", "_self");
                  }}
                >
                  {t("navigation.startDiscovery")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
